import { useEffect, useState, type PropsWithChildren } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createTRPCClient, httpLink } from "@trpc/client";
import type { AppRouter } from "@api-types/routers/routers";
import superjson from "superjson";
import { tryDecodeURIComponent } from "@/lib/url";
import { readDocumentCookies } from "@/lib/browserCookies";
import { getQueryClient, TRPCProvider } from "@/lib/trpc";
import { createRequestCorrelationId } from "@/lib/analytics/correlation";
import { AuthAnalyticsBridge } from "@/lib/analytics/AuthAnalyticsBridge";
import { startEngagementTimeTracking } from "@/lib/analytics/engagementTime";
import { startMarketingEventOutboxPoller } from "@/lib/analytics/marketingEventOutbox";
import { configureMarketingObservations } from "@/lib/analytics/marketingObservations";
import { getMarketingSessionId, MARKETING_SESSION_HEADER_NAME } from "@/lib/analytics/marketingSession";
import { AuthProvider } from "@/shared/context/AuthProvider";
import { AuthIdentityBoundary } from "@/shared/context/AuthIdentityBoundary";
import { FormatProvider } from "@/shared/context/FormatProvider";
import { ThemeProvider } from "@/shared/context/ThemeProvider";
import { ToastProvider } from "@/shared/context/ToastProvider";

const DEFAULT_BACKEND_URL = "http://localhost:3001";
const rawBackendUrl = import.meta.env.VITE_PUBLIC_API_URL?.trim();
const backendBaseUrl = rawBackendUrl && rawBackendUrl.length > 0 ? rawBackendUrl : DEFAULT_BACKEND_URL;
const normalizedBackendUrl = backendBaseUrl.replace(/\/+$/, "");
const trpcUrl = normalizedBackendUrl.endsWith("/trpc") ? normalizedBackendUrl : `${normalizedBackendUrl}/trpc`;

export const Providers = ({ children }: PropsWithChildren) => {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        // Not using batch link to make logs cleaner
        httpLink({
          url: trpcUrl,
          transformer: superjson,
          fetch(input: RequestInfo | URL, init?: RequestInit) {
            const existingHeaders = new Headers(init?.headers ?? {});
            const correlationId = createRequestCorrelationId();
            existingHeaders.set("x-correlation-id", correlationId);

            // Generic requests carry only the first-party journey. Provider-owned ids have an
            // explicit observation call and must never leak onto unrelated
            // product requests. Delete stale caller values defensively before setting the live
            // marketing session.
            existingHeaders.delete("x-ga-client-id");
            existingHeaders.delete("x-ga-session-id");
            existingHeaders.delete("x-segment-anonymous-id");
            existingHeaders.set(MARKETING_SESSION_HEADER_NAME, getMarketingSessionId());

            // Attach CSRF token when present for non-GET/HEAD requests
            try {
              const method = (init?.method ?? "GET").toString().toUpperCase();
              if (method !== "GET" && method !== "HEAD") {
                const csrf = resolveCsrfToken();
                if (csrf) {
                  existingHeaders.set("x-csrf-token", csrf);
                }
              }
            } catch {
              // ignore token resolution errors
            }

            return globalThis.fetch(input, {
              ...init,
              headers: existingHeaders,
              credentials: "include",
              // Analytics observations may be finishing during navigation. The event outbox
              // bounds each batch below the browser's keepalive payload budget.
              ...(isKeepaliveMarketingRequest(input) ? { keepalive: true } : {}),
            });
          },
        }),
      ],
    }),
  );

  useEffect(() => {
    // `sentAt` is stamped per attempt, so a retry carries its own; the API measures this
    // browser's clock offset from it and moves event and consent times onto its own clock.
    const stopObservations = configureMarketingObservations({
      recordConsent: (input, signal) =>
        trpcClient.marketing.recordWebAnalyticsConsent.mutate(
          { ...input, sentAt: new Date().toISOString() },
          { signal },
        ),
    });
    const stopPoller = startMarketingEventOutboxPoller((events, signal) =>
      trpcClient.marketing.recordWebAnalyticsEvents
        .mutate({ events, sentAt: new Date().toISOString() }, { signal })
        .then(({ results }) => results),
    );
    const stopEngagementTime = startEngagementTimeTracking();
    return () => {
      stopEngagementTime();
      stopPoller();
      stopObservations();
    };
  }, [trpcClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        <ThemeProvider>
          <FormatProvider>
            <ToastProvider>
              <AuthProvider>
                <AuthAnalyticsBridge />
                <AuthIdentityBoundary>{children}</AuthIdentityBoundary>
              </AuthProvider>
            </ToastProvider>
          </FormatProvider>
        </ThemeProvider>
      </TRPCProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

const SMALL_KEEPALIVE_MARKETING_PROCEDURES = new Set(["marketing.recordWebAnalyticsConsent"]);
const WEB_ANALYTICS_EVENTS_PROCEDURE = "marketing.recordWebAnalyticsEvents";

function isKeepaliveMarketingRequest(input: RequestInfo | URL): boolean {
  const value = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  try {
    const pathname = new URL(value, "http://localhost").pathname;
    const trpcMarkerIndex = pathname.lastIndexOf("/trpc/");
    if (trpcMarkerIndex < 0) return false;
    const procedures = decodeURIComponent(pathname.slice(trpcMarkerIndex + "/trpc/".length)).split(",");
    if (procedures.length === 1 && procedures[0] === WEB_ANALYTICS_EVENTS_PROCEDURE) return true;
    return (
      procedures.length > 0 && procedures.every((procedure) => SMALL_KEEPALIVE_MARKETING_PROCEDURES.has(procedure))
    );
  } catch {
    return false;
  }
}

function resolveCsrfToken(): string | null {
  // Prefer <meta name="csrf-token" content="..."> when present
  if (typeof document !== "undefined") {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    const metaVal = meta?.content?.trim();
    if (metaVal) return metaVal;
  }

  // Fallback to common cookie names
  const candidates = ["csrfToken", "XSRF-TOKEN", "_csrf", "_xsrf"];
  const cookie = readDocumentCookies();
  if (!cookie) return null;
  const parts = cookie.split(/;\s*/);
  for (const part of parts) {
    const idx = part.indexOf("=");
    const key = tryDecodeURIComponent(idx > -1 ? part.slice(0, idx) : part).trim();
    if (candidates.includes(key)) {
      const raw = idx > -1 ? part.slice(idx + 1) : "";
      return tryDecodeURIComponent(raw);
    }
  }

  return null;
}
